import { useCallback } from 'react';

import type { NavigateOptions } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';

import type { CancelableEventDetail, CancelableEventName } from 'src/shared/customEvents';
import { emitCancelableEvent } from 'src/shared/customEvents';

type CancelableNavigateEventNames = Extract<
	CancelableEventName,
	'shareChannelSelectedRedirect' | 'postSignatureRedirect'
>;

type CancelableNavigateConfig = {
	[T in CancelableNavigateEventNames]: {
		eventName: T;
		eventConfig: Omit<CancelableEventDetail<T>, 'originalRedirectFn'>;
		redirectUrl: string;
		navigateOptions?: NavigateOptions & {
			forceMode?: 'internal' | 'external';
		};
	};
}[CancelableNavigateEventNames];

/**
 * A hook that provides cancelable navigation functionality.
 *
 * This hook allows you to emit a cancelable event before navigation occurs.
 * If any event listener cancels the event, the navigation will be prevented.
 * Otherwise, the navigation proceeds as normal.
 *
 * @returns An object containing the cancelableNavigate function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { cancelableNavigate } = useCancelableNavigate();
 *
 *   const handleNavigation = () => {
 *     cancelableNavigate({
 *       redirectUrl: '/new-page',
 *       eventName: 'shareChannelSelectedRedirect',
 *       eventConfig: {
 *         // Additional event-specific configuration
 *         someProperty: 'value'
 *       },
 *       navigateOptions: { forceMode: 'external' } // optional
 *     });
 *   };
 *
 *   return <button onClick={handleNavigation}>Navigate</button>;
 * }
 * ```
 */
export function useCancelableNavigate(): {
	/**
	 * Performs a cancelable navigation operation.
	 *
	 * Emits a cancelable event with the provided configuration. If the event
	 * is not cancelled by any listener, navigation to the specified URL occurs.
	 * The event detail automatically includes an `originalRedirectFn` that can
	 * be called by event listeners to perform the original navigation.
	 *
	 * @param config - Configuration object for the cancelable navigation
	 * @param config.redirectUrl - The URL to navigate to if not cancelled
	 * @param config.eventName - The name of the cancelable event to emit
	 * @param config.eventConfig - Additional event configuration (originalRedirectFn is added automatically)
	 * @param config.navigateOptions - Optional navigate options (e.g., forceMode, replace, state)
	 */
	cancelableNavigate: (config: CancelableNavigateConfig) => void;
} {
	const navigate = useNavigate();

	const cancelableNavigate = useCallback(
		({ eventName, eventConfig, redirectUrl, navigateOptions }: CancelableNavigateConfig) => {
			const isNavigationCancelled = !emitCancelableEvent(eventName, {
				...eventConfig,
				originalRedirectFn: () => navigate(redirectUrl, navigateOptions),
			});
			if (isNavigationCancelled) {
				return;
			}
			navigate(redirectUrl, navigateOptions);
		},
		[navigate],
	);

	return {
		cancelableNavigate,
	};
}
